import { Alert, Button, Card, Input, Space } from 'antd'
import { CopyOutlined } from '@ant-design/icons'
import { useToast } from '@/contexts/ToastContext'
import ConnectionsTable from '../components/ConnectionsTable'
import { useMyMcpAccess } from '../hooks'
import { useDisconnectConnection } from '../useDisconnectConnection'

/** Renders nothing for anyone without MCP access, including while that is still loading. */
export default function McpProfileCard() {
  const { addToast } = useToast()
  const myAccess = useMyMcpAccess()
  const { confirmDisconnect, disconnectingId } = useDisconnectConnection()

  const data = myAccess.data
  const clients = data?.clients ?? []
  if (!data || (clients.length === 0 && data.connections.length === 0)) return null

  const copyConnectorUrl = async () => {
    try {
      await navigator.clipboard.writeText(data.connectorUrl)
      addToast('Connector URL copied', 'success')
    } catch {
      addToast('Couldn’t copy the URL. Select it and copy it yourself.', 'warning')
    }
  }

  return (
    <Card className="mt-6 rounded-xl border border-brand-100">
      <div className="mb-4">
        <h2 className="font-display text-lg font-bold text-gray-900">AI assistants</h2>
        <p className="text-sm text-gray-500">
          {clients.length > 0 ? (
            <>
              Use your DashboardInsights products from an AI assistant, only as far as you can here. It can
              make a few changes, such as adding findings, and checks with you first.
            </>
          ) : (
            'You can’t connect a client right now. You can still disconnect your connections below.'
          )}
        </p>
      </div>

      {clients.length > 0 && (
        <div className="grid gap-6 md:grid-cols-5">
          <div className="md:col-span-3 min-w-0">
            <h3 className="font-display text-sm font-semibold text-gray-900 mb-3">
              How to connect
            </h3>
            {data.connectorUrl ? (
              <>
                <ol className="space-y-3 text-sm text-gray-700 list-decimal pl-5">
                  <li>
                    In your AI assistant&rsquo;s settings, add a{' '}
                    <span className="font-semibold text-gray-900">custom connector</span>.
                  </li>
                  <li>
                    Paste the URL:
                    <Space.Compact block className="mt-2">
                      <Input value={data.connectorUrl} readOnly aria-label="Connector URL" className="font-mono text-xs" />
                      <Button icon={<CopyOutlined />} onClick={() => void copyConnectorUrl()}>
                        Copy
                      </Button>
                    </Space.Compact>
                  </li>
                  <li>Sign in when it asks, and choose the client.</li>
                </ol>
                <p className="mt-4 text-sm text-gray-500">
                  If you can&rsquo;t add connectors, ask whoever manages your organisation&rsquo;s AI assistant.
                </p>
              </>
            ) : (
              <Alert
                type="info"
                showIcon
                message="Connecting an AI assistant isn’t available yet."
                description="You have access, but DashboardInsights hasn’t finished setting up the connector. Check back later."
              />
          )}
        </div>

        <div className="md:col-span-2 min-w-0">
          <h3 className="font-display text-sm font-semibold text-gray-900 mb-3">Clients you can connect</h3>
          <ul className="space-y-2">
            {clients.map((client) => (
              <li key={client.clientId} className="text-sm">
                <div className="font-medium text-gray-800">{client.name}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      )}

      <div className="mt-6 pt-5 border-t border-brand-100">
        <h3 className="font-display text-sm font-semibold text-gray-900 mb-3">Your connections</h3>
        {myAccess.isError && (
          <Alert type="error" showIcon className="mb-4" message="Couldn’t refresh your connections." />
        )}
        <ConnectionsTable
          connections={data.connections}
          loading={myAccess.isFetching}
          showUser={false}
          showClient
          emptyText="You haven’t connected an AI assistant yet."
          disconnectingId={disconnectingId}
          onDisconnect={(connection) =>
            confirmDisconnect(connection, {
              title: `Disconnect ${connection.appName || 'this app'} from ${connection.clientName || 'this client'}?`,
              content: 'You can connect again while you still have access.',
              success: `Disconnected ${connection.appName || 'the app'}`,
            })
          }
        />
      </div>
    </Card>
  )
}
